import React from 'react'
import {Flex, IconButton, Input, useNumberInput, useStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {MinusIcon, AddIcon} from '../../components/icons'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {DeleteIcon} from '../icons/shared'
import {getPriceData} from '../../utils/product-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {remCalc} from '../../theme/_utils'

const QuantityPicker = ({
    productName,
    hideIncDecButtons = false,
    productData,
    onRemoveItemClick = () => {},
    variant,
    ariaLabel,
    // isReturnOrder = false,
    ...props
}) => {
    const style = useStyleConfig('QuantityPicker', {variant})
    const {addLayer} = useGoogleTagManager()
    const {formatMessage} = useIntl()
    const {currency} = useCurrency()

    const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
        ...props,
        focusInputOnChange: false,
        onFocus: (e) => {
            const {onFocus} = props
            e.target.select()
            onFocus?.call(this, e)
        },
    })

    const inc = getIncrementButtonProps({
        'aria-label': formatMessage(
            {
                defaultMessage: 'Increment Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_increment',
            },
            {productName},
        ),
    })

    const dec = getDecrementButtonProps({
        'aria-label': formatMessage(
            {
                defaultMessage: 'Decrement Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_decrement',
            },
            {productName},
        ),
    })

    const input = getInputProps({
        'aria-label': formatMessage({
            defaultMessage: 'Quantity',
            id: 'product_view.label.quantity',
        }),
    })

    // Accessibility: allow Space/Enter to trigger increment/decrement
    inc.tabIndex = 0
    dec.tabIndex = 0

    inc.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.preventDefault()
            evt.key = 'ArrowUp'
            input.onKeyDown(evt)
        }
    }

    dec.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.preventDefault()
            evt.key = 'ArrowDown'
            input.onKeyDown(evt)
        }
    }

    const handleRemoveItem = async () => {
        await addLayer(polish_ecommerce_gtm_event.bind(this))
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'remove_from_cart',
                currency: currency,
                bundle: 'false',
                items: [
                    {
                        item_name: productData?.c_defaultName,
                        item_id: productData?.productId,
                        item_category: productData?.c_defaultCategoryName,
                        item_variant: productData?.c_subModelIdentifier,
                        price: parseFloat(
                            (
                                (productData?.price - productData?.tax) /
                                productData?.quantity
                            ).toFixed(2),
                        ),
                        fullprice: getPriceData(productData)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        erp_category: productData?.c_MaterialGroup,
                        prdha_category: productData?.c_prdhaCategory,
                        quantity: productData?.quantity,
                    },
                ],
            }),
        )
        if (variant === 'freeItemPromo') {
            props.onChange?.('0', 0)
        } else {
            onRemoveItemClick(variant)
        }
    }

    return (
        <Flex
            tabIndex={-1}
            {...style?.container}
            data-testid="quantity-picker"
            aria-label={ariaLabel}
        >
            {!hideIncDecButtons &&
                (input.value > 1 ? (
                    <IconButton
                        variant="unstyled"
                        {...style?.incAndDecButton}
                        {...dec}
                        icon={<MinusIcon />}
                        data-testid="dec-button"
                        tabIndex={0}
                    />
                ) : (
                    <IconButton
                        variant="unstyled"
                        {...style?.incAndDecButton}
                        onClick={handleRemoveItem}
                        icon={<DeleteIcon />}
                        data-testid="cart-item-remove-button"
                        data-product-code={productData?.productId}
                        aria-label={formatMessage({
                            id: 'cart_secondary_button_group.action.remove.aria_label',
                            defaultMessage: 'Remove from cart',
                        })}
                        tabIndex={0}
                    />
                ))}

            <Input {...input} {...style?.input} data-testid="input" />

            {!hideIncDecButtons && (
                <IconButton
                    variant="unstyled"
                    {...style?.incAndDecButton}
                    {...inc}
                    icon={<AddIcon />}
                    aria-disabled={input.value >= props.max}
                    data-testid="inc-button"
                    tabIndex={0}
                    padding={remCalc(8)}
                />
            )}
        </Flex>
    )
}

QuantityPicker.propTypes = {
    productName: PropTypes.string,
    hideIncDecButtons: PropTypes.bool,
    variant: PropTypes.string,
    value: PropTypes.any,
    min: PropTypes.number,
    max: PropTypes.number,
    isReturnOrder: PropTypes.bool,
    productData: PropTypes.object,
    onRemoveItemClick: PropTypes.func,
    ariaLabel: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
}

export default QuantityPicker
